import { useState } from "react";
import { 
  Button, 
  Flex, 
  Input, 
  Select, 
  Space, 
  Table, 
  Typography, 
  Modal, 
  Form, 
  message, 
  Tooltip, 
  Tag,
  Image
} from "antd";
import { ReloadOutlined, EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import RobinPage from "../components/RobinPage";
import { gql, useQuery, useMutation } from "@apollo/client";

const { Text } = Typography;

const ALL_BENEFICIARIES = gql`
  query AllBeneficiaries(
    $pagination: PaginationInput
    $filter: BeneficiaryFilterInput
  ) {
    allBeneficiaries(pagination: $pagination, filter: $filter) {
      hasMore
      limit
      offset
      total
      beneficiaries {
        bank
        bankCode
        iban
        id
        name
        status
        ibanPhoto {
          url
        }
      }
    }
  }
`;

const UPDATE_BENEFICIARY_STATUS = gql`
  mutation UpdateBeneficiaryStatus($input: UpdateBeneficiaryStatusDto!) {
    updateBeneficiaryStatus(input: $input) {
      bank
      bankCode
      createdAt
      iban
      id
      name
      status
      updatedAt
    }
  }
`;

const bankCodes = {
  '10': 'Al Rajhi Bank',
  '15': 'Riyad Bank',
  '20': 'National Commercial Bank (NCB/Alahli)',
  '30': 'Saudi British Bank (SABB)',
  '40': 'Banque Saudi Fransi',
  '45': 'Saudi Investment Bank',
  '50': 'Arab National Bank',
  '55': 'Bank AlJazira',
  '57': 'Emirates NBD',
  '60': 'Bank AlBilad',
  '65': 'Saudi National Bank',
  '80': 'First Abu Dhabi Bank',
  '90': 'Saudi Post',
};

type Beneficiary = {
  bank?: string | null;
  bankCode?: string | null;
  iban?: string | null;
  id?: number | null;
  name?: string | null;
  status?: string | null;
  ibanPhoto?: {
    url?: string | null;
  } | null;
} | null;

type AllBeneficiariesResponse = {
  allBeneficiaries?: {
    hasMore?: boolean | null;
    limit?: number | null;
    offset?: number | null;
    total?: number | null;
    beneficiaries?: Beneficiary[] | null;
  } | null;
};

type BeneficiaryStatus = "PENDING" | "APPROVED" | "REJECTED";

const BENEFICIARY_STATUS_OPTIONS: BeneficiaryStatus[] = [
  "PENDING",
  "APPROVED",
  "REJECTED",
];

function BeneficiariesList() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState<BeneficiaryStatus | undefined>();
  const [bankCodeFilter, setBankCodeFilter] = useState<string | undefined>();
  const [searchFilter, setSearchFilter] = useState("");
  
  // Modal state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<{ id: number; name: string } | null>(null);

  const { data, loading, error, refetch } = useQuery<AllBeneficiariesResponse>(
    ALL_BENEFICIARIES,
    {
      variables: {
        pagination: undefined,
        filter: {
          statuses: statusFilter ? [statusFilter] : undefined,
          userId: null, // Only drivers (handled by backend)
          bankCode: bankCodeFilter || null,
          createdAfter: null,
          createdBefore: null,
          search: searchFilter || null,
        },
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  const [updateBeneficiaryStatus, { loading: updateLoading }] = useMutation(
    UPDATE_BENEFICIARY_STATUS,
    {
      onCompleted: () => {
        message.success(t("message.success.data_updated"));
        setIsModalVisible(false);
        form.resetFields();
        setSelectedBeneficiary(null);
        refetch();
      },
      onError: (error) => {
        message.error(error.message || t("message.error.data_update_failed"));
      },
    }
  );

  const handleOpenModal = (beneficiary: { id: number; name: string }) => {
    setSelectedBeneficiary(beneficiary);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedBeneficiary(null);
    form.resetFields();
  };

  const handleSubmitStatus = async (values: any) => {
    if (!selectedBeneficiary) return;

    await updateBeneficiaryStatus({
      variables: {
        input: {
          id: selectedBeneficiary.id,
          status: values.status,
        },
      },
    });
  };

  const getStatusColor = (status?: string | null) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return "success";
      case "PENDING":
        return "warning";
      case "REJECTED":
        return "error";
      default:
        return "default";
    }
  };

  const beneficiaries: Beneficiary[] = data?.allBeneficiaries?.beneficiaries ?? [];

  const dataSource = beneficiaries.map((beneficiary: Beneficiary, index: number) => ({
    key: beneficiary?.id ?? index,
    id: beneficiary?.id ?? "-",
    name: beneficiary?.name ?? "-",
    bank: beneficiary?.bank ?? "-",
    bankCode: beneficiary?.bankCode ?? "-",
    iban: beneficiary?.iban ?? "-",
    status: beneficiary?.status ?? "-",
    ibanPhoto: beneficiary?.ibanPhoto?.url,
  }));

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: t("label.name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("label.beneficiary.bank"),
      dataIndex: "bank",
      key: "bank",
    },
    {
      title: t("label.beneficiary.bank_code"),
      dataIndex: "bankCode",
      key: "bankCode",
    },
    {
      title: t("label.beneficiary.iban"),
      dataIndex: "iban",
      key: "iban",
    },
    {
      title: t("label.status"),
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        if (!status || status === "-") return "-";
        return (
          <Tag color={getStatusColor(status)}>
            {t(`label.beneficiary.status.${status.toLowerCase()}`, { defaultValue: status })}
          </Tag>
        );
      },
    },
    {
      title: t("label.beneficiary.iban_photo"),
      dataIndex: "ibanPhoto",
      key: "ibanPhoto",
      render: (url: string) => {
        if (!url) return "-";
        return (
          <Image
            src={url}
            alt="IBAN Photo"
            width={50}
            height={50}
            style={{ objectFit: "cover", borderRadius: "4px" }}
            preview
          />
        );
      },
    },
    {
      title: t("label.actions"),
      key: "actions",
      width: 100,
      align: "center" as const,
      render: (_: any, record: any) => (
        <Tooltip title={t("button.change_status")}>
          <Button
            shape="circle"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal({ id: record.id, name: record.name })}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <RobinPage
      title={t("label.menu.driver_beneficiaries")}
      actions={
        <Space size="middle">
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            disabled={loading}
          >
            {t("button.refresh")}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Flex gap="middle" wrap="wrap" style={{ width: "100%" }}>
          <Input
            allowClear
            value={searchFilter}
            placeholder={t("placeholder.search")}
            onChange={(e) => setSearchFilter(e.target.value)}
            onPressEnter={() => refetch()}
            style={{ flex: "1 1 calc(33.333% - 8px)", minWidth: 200 }}
          />
          <Select
            allowClear
            value={statusFilter}
            placeholder={t("label.status")}
            options={BENEFICIARY_STATUS_OPTIONS.map((status) => ({
              value: status,
              label: t(`label.beneficiary.status.${status.toLowerCase()}`, { defaultValue: status }),
            }))}
            onChange={(value) => setStatusFilter(value)}
            style={{ flex: "1 1 calc(33.333% - 8px)", minWidth: 200 }}
          />
          <Select
            allowClear
            value={bankCodeFilter}
            placeholder={t("label.beneficiary.bank_code")}
            options={Object.entries(bankCodes).map(([code, name]) => ({
              value: code,
              label: `${code} - ${name}`,
            }))}
            onChange={(value) => setBankCodeFilter(value)}
            style={{ flex: "1 1 calc(33.333% - 8px)", minWidth: 200 }}
          />
          <Space.Compact style={{ flex: "1 1 calc(33.333% - 8px)", minWidth: 200 }}>
            <Button
              type="primary"
              onClick={() => refetch()}
              style={{ flex: 1 }}
            >
              {t("button.search")}
            </Button>
            <Button
              onClick={() => {
                setStatusFilter(undefined);
                setBankCodeFilter(undefined);
                setSearchFilter("");
                refetch();
              }}
              style={{ flex: 1 }}
            >
              {t("button.reset_filters")}
            </Button>
          </Space.Compact>
        </Flex>
      </Space>
      {error ? (
        <Typography.Text type="danger">
          {t("error.failed_to_load")}
        </Typography.Text>
      ) : null}
      <Table loading={loading} dataSource={dataSource} columns={columns} />

      {/* Update Status Modal */}
      <Modal
        title={t("label.beneficiary.change_status")}
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitStatus}
          initialValues={{ status: undefined }}
        >
          <Form.Item
            name="status"
            label={t("label.status")}
            rules={[
              { required: true, message: t("message.error.field_required") },
            ]}
          >
            <Select
              placeholder={t("label.status")}
              options={BENEFICIARY_STATUS_OPTIONS.map((status) => ({
                value: status,
                label: t(`label.beneficiary.status.${status.toLowerCase()}`, { defaultValue: status }),
              }))}
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={handleCloseModal}>
                {t("button.cancel")}
              </Button>
              <Button type="primary" htmlType="submit" loading={updateLoading}>
                {t("button.save")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </RobinPage>
  );
}

export default BeneficiariesList;
